import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

export default function AdPerformance({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchAdPerformance(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || (!data.topAds?.length && !data.bottomAds?.length)) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
            style={{ fontFamily: brand.typography?.headerFont }}>
          Your Ads
        </h3>
        <p className="text-gray-500 mt-2">No ad performance data available yet.</p>
      </div>
    );
  }

  const { topAds, bottomAds, currency: apiCurrency } = data;
  const currency = apiCurrency || brand?.currencySymbol || '$';

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-6"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Your Ads
      </h3>

      {topAds.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <h4 className="text-sm font-medium text-gray-600">Best performing</h4>
          </div>
          <div className="space-y-2">
            {topAds.map((ad, idx) => (
              <AdCard key={ad.adId || idx} ad={ad} rank={idx + 1} variant="top" currency={currency} />
            ))}
          </div>
        </div>
      )}

      {bottomAds.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <h4 className="text-sm font-medium text-gray-600">Needs attention</h4>
          </div>
          <div className="space-y-2">
            {bottomAds.map((ad, idx) => (
              <AdCard key={ad.adId || idx} ad={ad} rank={idx + 1} variant="bottom" currency={currency} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdCard({ ad, rank, variant, currency }) {
  const [expanded, setExpanded] = useState(false);
  const isTop = variant === 'top';

  const borderColor = isTop ? 'border-green-200' : 'border-red-200';
  const bgColor = isTop ? 'bg-green-50/50' : 'bg-red-50/50';
  const rankBg = isTop ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  const headlinePreview = (ad.headlines || []).slice(0, 3).join(' | ') || 'Ad';

  return (
    <div className={`border ${borderColor} ${bgColor} rounded-lg overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 text-left hover:bg-white/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <span className={`${rankBg} text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5`}>
              {rank}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{headlinePreview}</p>
              <p className="text-xs text-gray-400 truncate">{ad.campaign} &middot; {ad.adGroup}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0 text-right">
            <div>
              <p className={`text-sm font-semibold ${ad.enquiries > 0 ? 'text-gray-900' : 'text-red-600'}`}>
                {ad.enquiries > 0 ? `${ad.enquiries} enquiries` : '0 enquiries'}
              </p>
              <p className="text-xs text-gray-500">{ad.clicks} clicks</p>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200/50 p-4 bg-white/50">
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Ad preview</p>
            <div className="bg-white rounded border border-gray-200 p-3">
              <p className="text-blue-700 text-sm font-medium leading-snug">
                {(ad.headlines || []).join(' | ')}
              </p>
              {ad.descriptions?.length > 0 && (
                <p className="text-gray-600 text-xs mt-1 leading-relaxed">
                  {ad.descriptions.join(' ')}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded p-2">
              <p className="text-xs text-gray-500">Enquiries</p>
              <p className="font-semibold text-sm text-gray-900">{ad.enquiries}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <p className="text-xs text-gray-500">Clicks</p>
              <p className="font-semibold text-sm text-gray-900">{ad.clicks}</p>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <p className="text-xs text-gray-500">Cost</p>
              <p className="font-semibold text-sm text-gray-900">{currency}{ad.cost.toFixed(0)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
